import { useMemo, memo } from 'react';
import { useSelector } from 'react-redux';
import { trackDashboardInteraction } from 'FormsUI/utils/TrackingUtils';
import TrackingContextWrapper from 'ui-forms-components/contextProviders/TrackingContextWrapper';
import { FrameworkListingLibProvider } from 'framework-listing-lib';
import { IN } from 'framework-listing-lib/utils/filters';
import UsageTracker from 'FormsUI/utils/UsageTracker';
import { FORM_OBJECT_TYPE, PROPERTIES, TABLE_FORM_TYPES } from 'FormsUI/components/dashboard/frameworkTable/constants';
import { ListingPage } from 'foundations-components/transitional/components/ListingPage';
import { DashboardPage } from 'foundations-components/transitional/components/DashboardPage';
import { TABS } from 'FormsUI/constants/Dashboard';
import FormsDashboardHeader from 'FormsUI/components/dashboard/FormsDashboardHeader';
import useFormsFrameworkViews from 'FormsUI/components/dashboard/frameworkTable/hooks/useFormsFrameworkViews';
import useFrameworkUIModeProps from 'FormsUI/components/dashboard/frameworkTable/hooks/useFrameworkUIModeProps';
import useIsFormsZeroState from 'FormsUI/components/dashboard/frameworkTable/hooks/useIsFormsZeroState';
import usePropertyOverride from 'FormsUI/components/dashboard/frameworkTable/hooks/usePropertyOverride';
import DashboardBanner from 'ui-forms-components/DashboardBanner';
import ContactLimitBanner from 'FormsUI/components/dashboard/banners/contactLimitBanner/AsyncContactLimitBanner';
import UnregistredDomainContainer from 'FormsUI/lib/UnregistredDomain/components/AsyncUnregisteredDomainContainer';
import { isUngatedForFormTemplates } from 'FormsUI/selectors/Gates';
import AsyncMarketingHomePopup from 'FormsUI/loadable/AsyncMarketingHomePopup';
import AsyncPLGDashboardPage from 'FormsUI/loadable/AsyncPLGDashboardPage';
import FrameworkTable from 'FormsUI/components/dashboard/frameworkTable/FrameworkTable';
import { PopupFormDeprecationContainer } from 'FormsUI/components/dashboard/banners/popupForms/PopupFormDeprecationContainer';
import AnalyzeTab from 'FormsUI/components/dashboard/AnalyzeTab';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const trackerProps = {
  applicationId: 'forms-ui',
  tracker: UsageTracker
};
const SEARCH_INPUT_PROPS = {
  hasSearchInput: true
};
const hiddenFilter = {
  filterGroups: [{
    filters: [IN(PROPERTIES.FORM_TYPE, TABLE_FORM_TYPES)]
  }]
};
const ManageTab = /*#__PURE__*/memo(() => /*#__PURE__*/_jsxs("div", {
  "data-test-id": "manage-tab-content",
  children: [/*#__PURE__*/_jsx(DashboardBanner, {}), /*#__PURE__*/_jsx(UnregistredDomainContainer, {}), /*#__PURE__*/_jsx(ContactLimitBanner, {}), /*#__PURE__*/_jsx(FrameworkTable, {}), /*#__PURE__*/_jsx(AsyncMarketingHomePopup, {})]
}));
const FormsDashboard = /*#__PURE__*/memo(({
  tab = TABS.MANAGE,
  isComparisonAllowed = false,
  businessUnits = [],
  onTabChange = () => {},
  hasBusinessUnitAccess = false,
  ungatedForBusinessUnitPicker = false,
  ungatedForPLGEmptyState = false,
  ungatedForNewEditorAccess = false
}) => {
  const viewProps = useFormsFrameworkViews();
  const uiModeProps = useFrameworkUIModeProps();
  const propertyOverride = usePropertyOverride();
  const hasFormTemplatesGate = useSelector(isUngatedForFormTemplates);
  const {
    isZeroState: isPortalZeroState,
    loading: isLoadingZeroState
  } = useIsFormsZeroState();
  const isManageTab = tab === TABS.MANAGE;
  const headerComponent = useMemo(() => /*#__PURE__*/_jsx(FormsDashboardHeader, {
    tab: tab,
    onTabChange: onTabChange,
    hasBusinessUnitAccess: hasBusinessUnitAccess,
    hasFormTemplatesGate: hasFormTemplatesGate,
    isComparisonAllowed: isComparisonAllowed,
    isPortalZeroState: isPortalZeroState,
    ungatedForBusinessUnitPicker: ungatedForBusinessUnitPicker
  }), [tab, onTabChange, hasBusinessUnitAccess, hasFormTemplatesGate, isComparisonAllowed, isPortalZeroState, ungatedForBusinessUnitPicker]);
  const content = useMemo(() => isManageTab ? /*#__PURE__*/_jsx(ManageTab, {}) : /*#__PURE__*/_jsx(AnalyzeTab, {
    hasBusinessUnitAccess: hasBusinessUnitAccess,
    businessUnits: businessUnits,
    isPortalZeroState: isPortalZeroState,
    isLoadingZeroState: isLoadingZeroState
  }), [isManageTab, hasBusinessUnitAccess, businessUnits, isPortalZeroState, isLoadingZeroState]);
  const PageContainer = isManageTab ? ListingPage : DashboardPage;
  const pageLayout = isManageTab ? 'full-width' : undefined;
  const showPLGOnboarding = ungatedForPLGEmptyState && ungatedForNewEditorAccess && isPortalZeroState;
  if (showPLGOnboarding) {
    return /*#__PURE__*/_jsx(AsyncPLGDashboardPage, {});
  }
  const dashboardPage = /*#__PURE__*/_jsxs(PageContainer, {
    "data-selenium-id": "container",
    pageLayout: pageLayout,
    headerComponent: headerComponent,
    children: [/*#__PURE__*/_jsx(PopupFormDeprecationContainer, {}), content]
  });
  return /*#__PURE__*/_jsx(TrackingContextWrapper, {
    track: trackDashboardInteraction,
    children: /*#__PURE__*/_jsx("section", {
      children: isManageTab ? /*#__PURE__*/_jsx(FrameworkListingLibProvider, {
        objectType: FORM_OBJECT_TYPE,
        viewProps: viewProps,
        trackerProps: trackerProps,
        hiddenFilter: hiddenFilter,
        uiModeProps: uiModeProps,
        searchInputProps: SEARCH_INPUT_PROPS,
        propertyOverride: propertyOverride,
        children: dashboardPage
      }) : dashboardPage
    })
  });
});
export default FormsDashboard;